import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import PublishIcon from "@mui/icons-material/Publish";
import { Box, Button, Stack, Tooltip } from "@mui/material";
import type { ReactNode } from "react";

import type { SectionSpecState } from "../../models/SectionSpecState";

interface SectionSpecActionsProps {
	state: SectionSpecState;
	selectedId: number | null;
	disabled: boolean;
	hasAppliedItem: boolean;
	onSelect: (profileSpecId: number) => void;
	onCreateDraft: () => void;
	onEditDraft: (profileSpecId: number) => void;
	onDeleteDraft: (profileSpecId: number) => void;
	onConfirmDraft: (profileSpecId: number) => void;
	onSendToDraft: (profileSpecId: number) => void;
	onEditPublished: (profileSpecId: number) => void;
}

const WithReason = ({
	reason,
	children,
}: {
	reason: string | null;
	children: ReactNode;
}) => (
	<Tooltip describeChild title={reason ?? ""}>
		<Box
			component="span"
			tabIndex={reason ? 0 : undefined}
			sx={{ display: "inline-flex" }}
		>
			{children}
		</Box>
	</Tooltip>
);

export const SectionSpecActions = ({
	state,
	selectedId,
	disabled,
	hasAppliedItem,
	onSelect,
	onCreateDraft,
	onEditDraft,
	onDeleteDraft,
	onConfirmDraft,
	onSendToDraft,
	onEditPublished,
}: SectionSpecActionsProps) => {
	const pendingId =
		state.kind === "scheduled"
			? state.scheduledId
			: state.kind === "draft"
				? state.draftId
				: null;

	if (pendingId !== null && selectedId !== pendingId)
		return (
			<Button
				variant="outlined"
				size="small"
				disabled={disabled}
				data-test-id="see-pending-spec-button"
				onClick={() => onSelect(pendingId)}
			>
				{state.kind === "scheduled" ? "See Scheduled" : "See Draft"}
			</Button>
		);

	const createDraftButton = (
		<Button
			variant="outlined"
			size="small"
			startIcon={<AddIcon />}
			disabled={disabled}
			data-test-id="create-spec-draft-button"
			onClick={onCreateDraft}
		>
			Create Draft
		</Button>
	);

	switch (state.kind) {
		case "scheduled":
			return (
				<Button
					variant="outlined"
					size="small"
					disabled={disabled || !state.canSendToDraft}
					data-test-id="change-spec-to-draft-button"
					onClick={() => onSendToDraft(state.scheduledId)}
				>
					Change to Draft
				</Button>
			);
		case "draft":
			return (
				<Stack direction="row" gap={1}>
					<Button
						variant="outlined"
						color="error"
						size="small"
						startIcon={<DeleteOutlineIcon />}
						disabled={disabled}
						onClick={() => onDeleteDraft(state.draftId)}
					>
						Delete Draft
					</Button>
					<Button
						variant="outlined"
						size="small"
						startIcon={<EditOutlinedIcon />}
						disabled={disabled}
						data-test-id="edit-spec-draft-button"
						onClick={() => onEditDraft(state.draftId)}
					>
						Edit Draft
					</Button>
					<WithReason
						reason={
							!disabled && !hasAppliedItem
								? "At least one dimension must be applied before confirming the draft."
								: null
						}
					>
						<Button
							variant="contained"
							color="success"
							size="small"
							startIcon={<PublishIcon />}
							disabled={disabled || !hasAppliedItem}
							data-test-id="confirm-spec-draft-button"
							onClick={() => onConfirmDraft(state.draftId)}
						>
							Confirm
						</Button>
					</WithReason>
				</Stack>
			);
		case "published":
			return (
				<Stack direction="row" gap={1}>
					{state.canEditPublished && selectedId === state.currentId && (
						<>
							<WithReason reason={state.backToDraftBlockReason}>
								<Button
									variant="outlined"
									size="small"
									disabled={disabled || state.backToDraftBlockReason !== null}
									onClick={() => onSendToDraft(state.currentId)}
								>
									Back to Draft
								</Button>
							</WithReason>
							<Button
								variant="outlined"
								size="small"
								startIcon={<EditOutlinedIcon />}
								disabled={disabled}
								data-test-id="edit-published-spec-button"
								onClick={() => onEditPublished(state.currentId)}
							>
								Edit Published
							</Button>
						</>
					)}
					{createDraftButton}
				</Stack>
			);
		case "empty":
			return createDraftButton;
	}
};
